// ✅ Trekkin.app — usePanelsGPSBridge.jsx v2.4.0 (LIVE + PATHPOINT + MÉTRICAS)
// 🔗 Ponte única entre useGPSHybrid → usePanelsStore + LiveShare
// 🔹 Envia pos + stats + pathPoint (com elevação!)
// 🔹 Viewer passa a receber o rasto completo (gráfico funciona)
// 🔹 Retrocompatível — nada removido

import { useEffect, useRef } from "react";
import { usePanelsStore } from "./usePanelsStore.jsx";
import { useAuthStore } from "./useAuthStore";
import { useEventsStore } from "./useEventsStore.jsx";
import { useShareStore } from "./useShareStore.jsx";
import { useGroupStore } from "./useGroupStore.jsx";
import { logEvent } from "./useLogsStore.jsx";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import {
  ANDROID_TRACK_MAX_SPEED_MPS,
  filterAndroidTrackPoints,
  getAndroidTrackSequenceCoverage,
  shouldImmediatelyResyncAndroidTrack,
  shouldForceFullAndroidResumeSnapshot,
  shouldKeepAndroidTrackPoint,
  shouldMarkAndroidRecordingGap,
} from "../utils/androidTrackFilter.js";

const sharedAndroidRecentPointKeys = new Map();
const sharedAndroidElevationFilter = { value: null, ts: 0 };

function getActiveEventForGroupSafe(groups, gid, events) {
  if (!gid) return { group: null, activeEvent: null, isLive: false };

  const groupsArr = Array.isArray(groups)
    ? groups
    : groups && typeof groups === "object"
    ? Object.values(groups)
    : [];

  const group =
    groupsArr.find(
      (g) =>
        g.id === gid || g.groupId === gid || g.firebaseId === gid || g.key === gid
    ) || null;

  if (!group) return { group: null, activeEvent: null, isLive: false };

  const activeEventId =
    group.activeEventId || group.activeEvent || group.eventId || null;

  const eventsArr = Array.isArray(events)
    ? events
    : events && typeof events === "object"
    ? Object.values(events)
    : [];

  const activeEvent =
    activeEventId && eventsArr.length
      ? eventsArr.find((ev) => ev.id === activeEventId) || null
      : null;

  if (!activeEvent && activeEventId) {
    return {
      group,
      activeEvent: { id: activeEventId, isLive: true, __fallback: true },
      isLive: true,
    };
  }

  const isLive = !!activeEvent?.isLive;
  return { group, activeEvent, isLive };
}

export default function usePanelsGPSBridge() {
  const lastSeqRef = useRef(-1);
  const mountedRef = useRef(false);
  const forceSyncRef = useRef(false);
  const lastBufferSeqRef = useRef(-1);
  const pendingBufferRef = useRef([]);
  const lastPointTsRef = useRef(0);
  const lastPathSyncTsRef = useRef(0);
  const bufferDrainTimerRef = useRef(null);
  const appActiveRef = useRef(true);
  const lastGpsUpdateTsRef = useRef(0);
  const lastFullResyncTsRef = useRef(0);
  const androidRecordingWarmupRef = useRef(false);
  const androidRecordingStartTsRef = useRef(0);
  const androidRecordingWarmupOkTsRef = useRef(0);
  const androidForceGapRef = useRef(false);
  const androidPathActiveTsRef = useRef(0);
  const androidResumeTsRef = useRef(0);
  const androidResumeFenceTsRef = useRef(0);
  const androidResumeSkipLeftRef = useRef(0);
  const appStateLastTsRef = useRef(0);
  const appStateResumeHandledRef = useRef(0);
  const lastAppStateEventTsRef = useRef(0);
  const lastAppStateActiveRef = useRef(null);
  const androidResumeRequestAtRef = useRef(0);
  const androidNativeSeqRef = useRef(-1);
  const androidNativeLastPointRef = useRef(null);
  const androidNativePollTimerRef = useRef(null);
  const androidNativePollNowRef = useRef(null);
  const androidCompactResumeNowRef = useRef(null);
  const androidCompactResumeTimerRef = useRef(null);
  const androidRecordingGapLogRef = useRef({ ts: 0, count: 0 });
  const androidNativePollBusyRef = useRef(false);
  const androidResumeSyncInFlightRef = useRef(false);
  const androidFullResyncPendingRef = useRef(false);
  const androidQueuedFullResyncTimerRef = useRef(null);
  const androidDeferredResumeSyncTimerRef = useRef(null);
  const androidLastFullResumeSyncAtRef = useRef(0);
  const androidLastLargeBufferApplyRef = useRef({ ts: 0, seq: -1, points: 0 });
  const lastGpsBufferLogTsRef = useRef(0);
  const androidLastDuplicateBufferLogTsRef = useRef(0);
  const androidLastEmptyPollPerfLogTsRef = useRef(0);
  const dupSeqWarnRef = useRef({ ts: 0, count: 0 });
  const isAndroidRef = useRef(
    typeof window !== "undefined" && Capacitor?.getPlatform?.() === "android"
  );
  const GAP_SECONDS = isAndroidRef.current ? 30 : 60;
  const GAP_DISTANCE_METERS = isAndroidRef.current ? 50 : 80;
  const ANDROID_RECORDING_WARMUP_ACC = 20;
  const ANDROID_RECORDING_WARMUP_MAX_MS = 20000;
  const ANDROID_RECORDING_MAX_ACC = 40;
  const ANDROID_RECORDING_MAX_ACC_BUFFER = 60;
  const ANDROID_RECORDING_MAX_ACC_SOFT = 60;
  const ANDROID_RECORDING_MAX_ACC_BUFFER_SOFT = 60;
  const ANDROID_RECORDING_SOFT_ACC_GAP_MS = 30000;
  const ANDROID_BUFFER_OOO_MS = 30000;
  const ANDROID_RESUME_SKIP_POINTS = 1;
  const ANDROID_RESUME_SKIP_DISTANCE_METERS = 200;
  const ANDROID_RESUME_SKIP_SPEED_MPS = 8;
  const ANDROID_RESUME_GAP_MIN_SECONDS = 15;
  const MIN_POINT_INTERVAL_MS = isAndroidRef.current ? 200 : 1000;
  const BUFFER_DRAIN_INTERVAL_MS = 1500;
  const BUFFER_DRAIN_MAX_POINTS_FG = 60;
  const BUFFER_DRAIN_MAX_POINTS_BG = 10;
  const ANDROID_BUFFER_DIRECT_APPLY_MIN = 120;
  const ANDROID_BULK_FILTER_LOG_MIN = 40;
  const ANDROID_NATIVE_POLL_MS = 2000;
  const ANDROID_NATIVE_RENDER_WINDOW = 8000;
  const ANDROID_RESUME_INCREMENTAL_MIN_POINTS = 25;
  const ANDROID_RESUME_INCREMENTAL_MAX_STALE_MS = 30000;
  const ANDROID_RESUME_FULL_DEFER_MS = 8000;
  const ANDROID_RESUME_FULL_MIN_INTERVAL_MS = 5000;
  const ANDROID_RESUME_FG_POLL_QUIET_MS = 5000;
  const ANDROID_RESUME_COMPACT_MAX_POINTS = 160;
  const ANDROID_RESUME_COMPACT_PAINT_DELAY_MS = 120;
  const ANDROID_POINT_DEDUPE_TTL_MS = 2 * 60 * 1000;

  const logAndroidDrop = (reason, p, extra = {}) => {
    try {
      const payload = {
        reason,
        ts: p?.ts || p?.time || null,
        src: p?.src || null,
        seq: typeof p?.seq === "number" ? p.seq : null,
        lat: Number.isFinite(p?.lat) ? Number(p.lat) : null,
        lon: Number.isFinite(p?.lon) ? Number(p.lon) : null,
        acc: Number.isFinite(p?.acc) ? Math.round(p.acc) : null,
        ...extra,
      };
      logEvent("gps", "ANDROID_POINT_DROP", payload);
    } catch (e) {}
  };

  const ANDROID_PATH_ACTIVE_MS = 8000;

  const getRecordingMinTs = () => {
    const { recordingStartTime } = usePanelsStore.getState();
    const ts = Number(recordingStartTime) || 0;
    return ts ? ts - 2000 : 0;
  };

  const isAcceptableRecordingAcc = (p) => {
    if (!isAndroidRef.current) return true;
    const acc = Number(p?.acc);
    if (!Number.isFinite(acc)) return true;
    const isBufferLike = p?.fromBuffer || p?.src === "android-buffer" || p?.src === "android-bg";
    const ts = Number(p?.ts) || Date.now();
    const lastTs = Number(lastPointTsRef.current) || 0;
    const dt = lastTs ? ts - lastTs : 0;
    const softAcc =
      isBufferLike ? ANDROID_RECORDING_MAX_ACC_BUFFER_SOFT : ANDROID_RECORDING_MAX_ACC_SOFT;
    const baseAcc =
      isBufferLike ? ANDROID_RECORDING_MAX_ACC_BUFFER : ANDROID_RECORDING_MAX_ACC;
    const maxAcc = dt >= ANDROID_RECORDING_SOFT_ACC_GAP_MS ? softAcc : baseAcc;
    return acc <= maxAcc;
  };
  const isAndroidPathActive = () =>
    isAndroidRef.current &&
    androidPathActiveTsRef.current &&
    Date.now() - androidPathActiveTsRef.current < ANDROID_PATH_ACTIVE_MS;

  useEffect(() => {
    let listenerHandle = null;
    let isCancelled = false;

    const requestAndroidRecordingResumeSync = (source) => {
      if (!isAndroidRef.current) return false;
      const nowTs = Date.now();
      const storeState = usePanelsStore.getState();
      const isRecording = !!storeState?.isRecording;
      const isRecordingPaused = !!storeState?.isRecordingPaused;
      if (!isRecording || isRecordingPaused) return false;
      const pointsNow = Array.isArray(storeState?.points) ? storeState.points.length : 0;
      const storeLastPoint = pointsNow > 0 ? storeState.points[pointsNow - 1] : null;
      const storeLastSeq = Number(storeLastPoint?.seq);
      const storeSequenceStats = getAndroidTrackSequenceCoverage(storeState?.points);
      const hasSparseSequenceCoverage = storeSequenceStats.sparse;
      const lastStoreTs =
        pointsNow > 0 ? Number(storeState.points[pointsNow - 1]?.ts) || 0 : 0;
      const lastSeq = Math.max(
        androidNativeSeqRef.current ?? -1,
        Number.isFinite(storeLastSeq) ? storeLastSeq : -1
      );
      if (lastSeq > (androidNativeSeqRef.current ?? -1)) {
        androidNativeSeqRef.current = lastSeq;
      }
      const lastKnownTs = Number(lastPointTsRef.current || lastStoreTs || 0);
      const staleMs = lastKnownTs ? nowTs - lastKnownTs : Number.POSITIVE_INFINITY;
      const backgroundMs = appStateLastTsRef.current
        ? Math.max(0, nowTs - appStateLastTsRef.current)
        : 0;
      // A first foreground fix can advance `lastSeq` before the buffered
      // background fixes are merged. Delta polling from that sequence then
      // returns nothing and visually joins the old tail to the current fix.
      const resumedAfterBackground = shouldForceFullAndroidResumeSnapshot(
        backgroundMs
      );
      const canResumeIncremental =
        !resumedAfterBackground &&
        pointsNow >= ANDROID_RESUME_INCREMENTAL_MIN_POINTS &&
        lastSeq >= 0 &&
        lastKnownTs &&
        staleMs >= 0 &&
        staleMs <= ANDROID_RESUME_INCREMENTAL_MAX_STALE_MS &&
        !hasSparseSequenceCoverage;
      const needsFullResync = !canResumeIncremental;
      const canPollDelta = lastSeq >= 0 && !needsFullResync;
      const needsImmediateFullResync =
        staleMs > ANDROID_RESUME_INCREMENTAL_MAX_STALE_MS ||
        shouldImmediatelyResyncAndroidTrack(
          storeState?.points,
          lastSeq,
          { minPoints: ANDROID_RESUME_INCREMENTAL_MIN_POINTS }
        );
      const reason = source === "visibility"
        ? "resume_visibility_delta"
        : "resume_delta";

      if (nowTs - androidResumeRequestAtRef.current < 10000) {
        logEvent("system", "Android resume sync: duplicate skipped", {
          source,
          elapsedMs: nowTs - androidResumeRequestAtRef.current,
        });
        return true;
      }

      androidResumeRequestAtRef.current = nowTs;
      androidResumeTsRef.current = nowTs;
      androidResumeFenceTsRef.current = lastPointTsRef.current || 0;
      androidPathActiveTsRef.current = 0;
      forceSyncRef.current = true;
      androidForceGapRef.current = false;
      androidResumeSkipLeftRef.current = 0;
      androidResumeSyncInFlightRef.current = canPollDelta || needsFullResync;
      androidFullResyncPendingRef.current = false;

      logEvent("system", "Android resume sync: recording delta", {
        source,
        pointsNow,
        fenceTs: androidResumeFenceTsRef.current || 0,
        lastSeq,
        staleMs: Number.isFinite(staleMs) ? Math.round(staleMs) : null,
        backgroundMs,
        resumedAfterBackground,
        canPollDelta,
        needsFullResync,
        sequenceCount: storeSequenceStats.count,
        sequenceSpan: storeSequenceStats.span,
        sequenceMaxGap: storeSequenceStats.maxGap,
        sequenceCoverage: Number(storeSequenceStats.coverage.toFixed(3)),
        sparseSequenceCoverage: hasSparseSequenceCoverage,
        immediateFullResync: needsImmediateFullResync,
      });

      if (needsFullResync) {
        if (androidCompactResumeTimerRef.current) {
          clearTimeout(androidCompactResumeTimerRef.current);
        }
        androidCompactResumeTimerRef.current = setTimeout(() => {
          androidCompactResumeTimerRef.current = null;
          const compactResume = androidCompactResumeNowRef.current;
          if (!compactResume) {
            androidNativePollNowRef.current?.({
              reason: "resume_full_no_compact_api",
              force: true,
              reset: true,
            });
            return;
          }
          compactResume({ source, pointsNow, lastSeq }).then((applied) => {
            if (applied) {
              // The compact API only drains the native in-memory buffer. It is
              // useful for a quick first paint, but it is not the complete
              // recording and must never become the authoritative polyline.
              androidLastFullResumeSyncAtRef.current = Date.now();
              androidResumeSyncInFlightRef.current = true;
              logEvent("system", "Android resume sync: compact preview", {
                source,
                pointsNow,
                previousSeq: lastSeq,
              });
              androidNativePollNowRef.current?.({
                reason: "resume_full_after_compact",
                force: true,
                reset: true,
                resumeSync: true,
              });
              return;
            }
            androidLastFullResumeSyncAtRef.current = Date.now();
            androidResumeSyncInFlightRef.current = true;
            androidNativePollNowRef.current?.({
              reason: "resume_full_compact_fallback",
              force: true,
              reset: true,
            });
          });
        }, ANDROID_RESUME_COMPACT_PAINT_DELAY_MS);
      }

      if (canPollDelta) {
        androidNativePollNowRef.current?.({
          reason,
          force: true,
          reset: false,
        });
      }
      return true;
    };

    const handleVisibilityResume = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState !== "visible") return;
      requestAndroidRecordingResumeSync("visibility");
    };

    const register = async () => {
      const handle = await App.addListener("appStateChange", ({ isActive }) => {
        const nowTs = Date.now();
        if (
          lastAppStateActiveRef.current === isActive &&
          nowTs - lastAppStateEventTsRef.current < 2000
        ) {
          return;
        }
        lastAppStateActiveRef.current = isActive;
        lastAppStateEventTsRef.current = nowTs;
        appActiveRef.current = !!isActive;
        const perfNow =
          typeof performance !== "undefined" ? performance.now() : 0;
        if (isActive) {
          if (nowTs - appStateResumeHandledRef.current < 1000) {
            console.log(
              `[APPSTATE] resume ignored (debounce) ts=${nowTs} perf=${perfNow}`
            );
            return;
          }
          appStateResumeHandledRef.current = nowTs;
          const sinceBgMs = appStateLastTsRef.current
            ? nowTs - appStateLastTsRef.current
            : null;
          console.log(
            `[APPSTATE] resume ts=${nowTs} perf=${perfNow} sinceBgMs=${sinceBgMs}`
          );
          if (isAndroidRef.current && typeof window !== "undefined") {
            const storePoints = usePanelsStore.getState()?.points;
            const activeResumePerf = window.__TREKKIN_ANDROID_RESUME_PERF;
            const preserveVisibleResume =
              Number(activeResumePerf?.startedAt || 0) > 0 &&
              nowTs - Number(activeResumePerf.startedAt) < 5000;
            window.__TREKKIN_ANDROID_FOREGROUND_AT = nowTs;
            window.__TREKKIN_ANDROID_RESUME_PERF = {
              ...(preserveVisibleResume ? activeResumePerf : {}),
              startedAt: preserveVisibleResume
                ? Number(activeResumePerf.startedAt)
                : nowTs,
              sinceBgMs,
              pointsBefore: Array.isArray(storePoints) ? storePoints.length : 0,
              firstRenderLogged: preserveVisibleResume
                ? !!activeResumePerf.firstRenderLogged
                : false,
            };
            logEvent("system", "ANDROID_RESUME_PERF_START", {
              sinceBgMs,
              pointsBefore: window.__TREKKIN_ANDROID_RESUME_PERF.pointsBefore,
              preserveVisibleResume,
              elapsedSinceVisibleMs: preserveVisibleResume
                ? nowTs - Number(activeResumePerf.startedAt)
                : null,
            });
          }
          setTimeout(() => {
            const perfTick =
              typeof performance !== "undefined" ? performance.now() : 0;
            const mainThreadDelayMs = Math.round(perfTick - perfNow);
            console.log(
              `[APPSTATE] resume+tick perf=${perfTick} deltaMs=${Math.round(
                perfTick - perfNow
              )}`
            );
            if (isAndroidRef.current) {
              logEvent("system", "ANDROID_RESUME_PERF_MAIN_THREAD_TICK", {
                mainThreadDelayMs,
                pointsNow: Array.isArray(usePanelsStore.getState()?.points)
                  ? usePanelsStore.getState().points.length
                  : 0,
              });
            }
          }, 0);
        } else {
          appStateLastTsRef.current = nowTs;
          console.log(`[APPSTATE] background ts=${nowTs} perf=${perfNow}`);
        }
        const { isRecording, isRecordingPaused } = usePanelsStore.getState();
        if (isActive) {
          console.log("🔄 App resumed → reset seq");
          if (isAndroidRef.current) {
            androidResumeTsRef.current = Date.now();
            androidResumeFenceTsRef.current = lastPointTsRef.current || 0;
            androidPathActiveTsRef.current = 0;
            forceSyncRef.current = true; // force sync after BG buffer flush
            if (isRecording && !isRecordingPaused) {
              // Durante gravação queremos continuidade total (sem gaps forçados).
              androidForceGapRef.current = false;
              androidResumeSkipLeftRef.current = 0;
            }

            if (!isRecording) {
              androidResumeRequestAtRef.current = 0;
              lastSeqRef.current = -1;
              lastPointTsRef.current = 0;
              androidNativeSeqRef.current = -1;
              androidNativeLastPointRef.current = null;
              androidForceGapRef.current = true; // quebra visual após resume
              logEvent("system", "Android appStateChange: active");
            } else {
              requestAndroidRecordingResumeSync("app_state");
            }
          } else {
            lastSeqRef.current = -1;
            lastPointTsRef.current = 0;
          }
        } else if (isAndroidRef.current) {
          if (isRecording && !isRecordingPaused) {
            const { points } = usePanelsStore.getState();
            const lastTs = Number(points?.[points.length - 1]?.ts) || 0;
            if (lastTs && lastTs > lastPointTsRef.current) {
              lastPointTsRef.current = lastTs;
            }
            logEvent("system", "Android appStateChange: inactive (recording)");
          } else {
            lastSeqRef.current = -1;
            lastPointTsRef.current = 0;
            logEvent("system", "Android appStateChange: inactive");
          }
        }
      });

      listenerHandle = handle;
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityResume);
    }

    register();

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityResume);
      }
      if (androidDeferredResumeSyncTimerRef.current) {
        clearTimeout(androidDeferredResumeSyncTimerRef.current);
        androidDeferredResumeSyncTimerRef.current = null;
      }
      if (androidCompactResumeTimerRef.current) {
        clearTimeout(androidCompactResumeTimerRef.current);
        androidCompactResumeTimerRef.current = null;
      }
      try {
        listenerHandle?.remove?.();
      } catch {}
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    console.log("🔗 usePanelsGPSBridge montado v2.4.0");
    const liveLog = (...args) => {
      try {
        if (localStorage.getItem("gpsLiveDebug") === "1") {
          console.log("[GPS-LIVE]", ...args);
        }
      } catch {}
    };

    function extractFix(detail) {
      if (!detail) return null;

      const candidates = [
        detail.lastFix,
        detail.fix,
        detail.position,
        detail.current,
        detail.pos,
      ];

      for (const c of candidates) {
        if (c && typeof c.lat === "number" && typeof c.lon === "number") {
          return c;
        }
      }

      if (Array.isArray(detail.path) && detail.path.length > 0) {
        const last = detail.path[detail.path.length - 1];
        if (last && typeof last.lat === "number" && typeof last.lon === "number")
          return last;
      }

      return null;
    }

    function toPoint(p) {
      if (!p || typeof p.lat !== "number" || typeof p.lon !== "number") return null;
      const rawTs = Number(p.ts || p.time || Date.now());
      const normalizedTs = rawTs && rawTs < 1e12 ? rawTs * 1000 : rawTs;
      const rawSpeed = Number(p.spd ?? p.speed);
      const rawSpeedAccuracy = Number(
        p.spdAcc ?? p.speedAcc ?? p.speedAccuracy
      );
      return {
        lat: p.lat,
        lon: p.lon,
        ele: Number.isFinite(p.alt ?? p.ele) ? (p.alt ?? p.ele) : null,
        acc: Number.isFinite(p.acc) ? p.acc : null,
        spd: Number.isFinite(rawSpeed) ? rawSpeed : null,
        spdAcc: Number.isFinite(rawSpeedAccuracy) ? rawSpeedAccuracy : null,
        ts: normalizedTs || Date.now(),
        src: p.src || null,
        fromBuffer: p.src === "android-buffer",
        seq: typeof p.seq === "number" ? p.seq : null,
      };
    }

    function getAndroidPointKey(p) {
      const ts = Number(p?.ts || p?.time || 0);
      const lat = Number(p?.lat);
      const lon = Number(p?.lon ?? p?.lng);
      if (!ts || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return `${ts}|${lat.toFixed(7)}|${lon.toFixed(7)}`;
    }

    function isDuplicateAndroidPoint(p) {
      if (!isAndroidRef.current) return false;
      const key = getAndroidPointKey(p);
      if (!key) return false;

      const now = Date.now();
      const recent = sharedAndroidRecentPointKeys;
      const previous = recent.get(key);
      recent.set(key, now);

      if (recent.size > 2000) {
        for (const [recentKey, seenAt] of recent) {
          if (now - seenAt > ANDROID_POINT_DEDUPE_TTL_MS) recent.delete(recentKey);
        }
      }
      return previous != null;
    }

    function smoothAndroidElevationPoint(point, filterState = sharedAndroidElevationFilter) {
      if (!isAndroidRef.current || !point) return point;
      const raw = Number(point.ele);
      if (!Number.isFinite(raw)) return point;

      const ts = Number(point.ts) || Date.now();
      if (!Number.isFinite(filterState.value) || !filterState.ts) {
        filterState.value = raw;
        filterState.ts = ts;
        return point;
      }
      if (ts <= filterState.ts) return point;

      const dtSec = Math.max(0.001, (ts - filterState.ts) / 1000);
      const delta = raw - filterState.value;
      const impossibleJump =
        Math.abs(delta) > 80 || (dtSec <= 5 && Math.abs(delta) / dtSec > 12);
      filterState.ts = ts;

      if (impossibleJump) {
        return { ...point, ele: filterState.value };
      }

      filterState.value = raw;
      return point;
    }

    function smoothAndroidElevationSeries(points, seedPoint = null) {
      const localState = {
        value: Number.isFinite(seedPoint?.ele) ? Number(seedPoint.ele) : null,
        ts: Number(seedPoint?.ts) || 0,
      };
      return points.map((point) => smoothAndroidElevationPoint(point, localState));
    }

    function syncLiveMemberLocation(fix) {
      if (!fix || typeof fix.lat !== "number" || typeof fix.lon !== "number") return;

      const groupState = useGroupStore.getState();
      const {
        currentGroup,
        groups,
        updateMyLocationInGroup,
        clearMemberLocation,
      } = groupState;

      const gid =
        typeof currentGroup === "string"
          ? currentGroup
          : currentGroup?.id || null;

      if (!gid || !updateMyLocationInGroup) return;

      const { events } = useEventsStore.getState() || {};

      const { activeEvent, isLive } = getActiveEventForGroupSafe(
        groups,
        gid,
        events
      );

      if (!isLive || !activeEvent) return;

      const authUser = useAuthStore.getState().user;
      const uid = authUser?.uid || null;

      const isGuide =
        uid &&
        (activeEvent.guideId === uid ||
          activeEvent.guideUid === uid ||
          activeEvent.guide === uid ||
          activeEvent.coGuideId === uid ||
          activeEvent.coGuideUid === uid);

      const shareGuideInLive = activeEvent?.shareGuideInLive !== false;

      if (isGuide && !shareGuideInLive) {
        if (gid && uid && clearMemberLocation) {
          clearMemberLocation(gid, uid).catch((err) => {
            console.warn("⚠️ Falha ao limpar localização do guia:", err);
          });
        }
        return;
      }

      updateMyLocationInGroup({
        lat: fix.lat,
        lon: fix.lon,
        acc: Number.isFinite(fix.acc) ? fix.acc : 0,
      }).catch((err) => {
        console.warn("⚠️ Falha ao atualizar localização LIVE:", err);
      });
    }

    function isReasonableStep(prev, next) {
      if (!isAndroidRef.current) return true;
      if (!prev || !next) return true;
      const dt = Math.max(0, (Number(next.ts) || 0) - (Number(prev.ts) || 0)) / 1000;
      if (dt >= GAP_SECONDS) return true;
      if (dt <= 0) return true;
      const dist = haversine(prev, next);
      const speedMps = dist / dt;
      // Keep valid vehicle-speed samples. Activity eligibility is evaluated
      // separately; this guard is only for genuine GPS teleports.
      if (speedMps > ANDROID_TRACK_MAX_SPEED_MPS) return false;
      if (!shouldKeepAndroidTrackPoint(prev, next, {
        gapMs: GAP_SECONDS * 1000,
        maxPlausibleSpeedMps: ANDROID_TRACK_MAX_SPEED_MPS,
      })) {
        return false;
      }
      const acc = Number(next?.acc);
      const goodAcc = Number.isFinite(acc) && acc <= 15;
      if (goodAcc) return true;
      if (dt < 2 && dist > 90) return false;
      if (dt < 5 && dist > 200) return false;
      return true;
    }

    function haversine(a, b) {
      const R = 6371000;
      const toRad = (x) => (x * Math.PI) / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLon = toRad(b.lon - a.lon);
      const la1 = toRad(a.lat);
      const la2 = toRad(b.lat);
      const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    }

    function mergeAndFilterAndroidPath(points, options = {}) {
      if (!Array.isArray(points) || !points.length) return [];
      const bulk = options.bulk === true;
      const dropStats = bulk
        ? {
            unreasonable_step_merge: 0,
            samples: [],
          }
        : null;

      const chronological = filterAndroidTrackPoints(points)
        .filter(Boolean)
        .sort((a, b) => (Number(a?.ts) || 0) - (Number(b?.ts) || 0));
      const accepted = [];
      const seen = new Set();

      for (const point of chronological) {
        const ts = Number(point?.ts) || 0;
        if (!ts) continue;

        const key = `${ts}:${Number(point?.lat).toFixed(6)}:${Number(point?.lon).toFixed(6)}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const prev = accepted[accepted.length - 1] || null;
        if (prev && !isReasonableStep(prev, point)) {
          const distanceM = Math.round(haversine(prev, point));
          if (dropStats) {
            dropStats.unreasonable_step_merge += 1;
            if (dropStats.samples.length < 5) {
              dropStats.samples.push({
                seq: typeof point?.seq === "number" ? point.seq : null,
                ts: Number(point?.ts) || null,
                lat: Number.isFinite(point?.lat) ? Number(point.lat) : null,
                lon: Number.isFinite(point?.lon) ? Number(point.lon) : null,
                acc: Number.isFinite(point?.acc) ? Math.round(point.acc) : null,
                prevTs: Number(prev?.ts) || null,
                distanceM,
              });
            }
          } else {
            logAndroidDrop("unreasonable_step_merge", point, {
              prevTs: Number(prev?.ts) || null,
              distanceM,
            });
          }
          continue;
        }
        accepted.push(prev ? markGapIfNeeded(point, prev) : point);
      }

      if (dropStats && dropStats.unreasonable_step_merge > 0) {
        logEvent("gps", "ANDROID_POINT_DROP_BULK", {
          reason: "unreasonable_step_merge",
          count: dropStats.unreasonable_step_merge,
          input: chronological.length,
          accepted: accepted.length,
          source: options.source || null,
          samples: dropStats.samples,
        });
      }

      return smoothAndroidElevationSeries(accepted);
    }

    function isAndroidBufferLike(point) {
      if (!isAndroidRef.current) return false;
      if (point?.fromBuffer) return true;
      const src = point?.src || "";
      return src === "android-bg" || src === "android-buffer";
    }

    function shouldAcceptByTime(nextTs) {
      const ts = Number(nextTs) || Date.now();
      if (ts < lastPointTsRef.current) return false;
      if (ts - lastPointTsRef.current < MIN_POINT_INTERVAL_MS) return false;
      lastPointTsRef.current = ts;
      return true;
    }

    function emitAndroidRender(points, reset = false, meta = {}) {
      if (!isAndroidRef.current) return false;
      if (!Array.isArray(points)) return;
      if (!points.length && !reset) return;
      try {
        const detail = {
          points,
          ...meta,
          preferEventPoints: reset,
        };
        window.dispatchEvent(
          new CustomEvent(reset ? "gps:render-reset" : "gps:render", { detail })
        );
      } catch {}
    }

    function sampleAndroidRenderPath(points) {
      if (!Array.isArray(points)) return [];
      if (points.length <= ANDROID_NATIVE_RENDER_WINDOW) return points;
      const step = Math.ceil(points.length / ANDROID_NATIVE_RENDER_WINDOW);
      const sampled = [];
      for (let i = 0; i < points.length; i += step) {
        sampled.push(points[i]);
      }
      const last = points[points.length - 1];
      if (sampled[sampled.length - 1] !== last) sampled.push(last);
      return sampled;
    }

    function consumeAndroidNativePoints(rawPoints, opts = {}) {
      if (!isAndroidRef.current) return;
      if (!Array.isArray(rawPoints) || !rawPoints.length) return;
      const recordingState = usePanelsStore.getState();
      if (!recordingState?.isRecording || recordingState?.isRecordingPaused) {
        logEvent("gps", "ANDROID_NATIVE_POINTS_DROP_NOT_RECORDING", {
          count: rawPoints.length,
          reason: opts.reason || "unknown",
          isRecording: !!recordingState?.isRecording,
          isRecordingPaused: !!recordingState?.isRecordingPaused,
        });
        return;
      }

      const minTs = getRecordingMinTs();
      const normalizeTs = (val) => {
        const n = Number(val) || 0;
        return n && n < 1e12 ? n * 1000 : n;
      };
      const mapped = rawPoints
        .filter((p) => {
          if (!minTs) return true;
          const ts = normalizeTs(p?.ts || p?.time || 0);
          return ts && ts >= minTs;
        })
        .map(toPoint)
        .filter(Boolean);
      if (!mapped.length) return;

      const sorted = smoothAndroidElevationSeries(
        mapped.sort((a, b) => {
          const aSeq = typeof a?.seq === "number" ? a.seq : null;
          const bSeq = typeof b?.seq === "number" ? b.seq : null;
          if (aSeq != null && bSeq != null) return aSeq - bSeq;
          if (aSeq != null) return -1;
          if (bSeq != null) return 1;
          return (Number(a?.ts) || 0) - (Number(b?.ts) || 0);
        }),
        androidNativeLastPointRef.current
      );

      const lastSeq = androidNativeSeqRef.current;
      const lastTs = lastPointTsRef.current || 0;
      let nextPoints = opts.reset ? [...sorted] : [];

      if (!opts.reset) {
        for (const p of sorted) {
          const seq = typeof p?.seq === "number" ? p.seq : null;
          if (seq != null) {
            if (lastSeq >= 0 && seq <= lastSeq) continue;
          } else if (lastTs && (Number(p?.ts) || 0) <= lastTs) {
            continue;
          }
          nextPoints.push(p);
        }
      }

      if (!nextPoints.length) return;

      const storeState = usePanelsStore.getState();
      const current = Array.isArray(storeState.points) ? storeState.points : [];
      let forceIncrementalFromReset = false;
      if (opts.reset && current.length && nextPoints.length > 50) {
        const currentLooksComplete =
          current.length >= Math.max(2, Math.floor(sorted.length * 0.75));
        const fresh = sorted.filter((p) => {
          const seq = typeof p?.seq === "number" ? p.seq : null;
          if (seq != null) return lastSeq >= 0 ? seq > lastSeq : true;
          const ts = Number(p?.ts) || 0;
          return lastTs ? ts > lastTs : true;
        });
        if (!fresh.length && lastSeq >= 0) {
          if (!currentLooksComplete) {
            logEvent("gps", "ANDROID_NATIVE_RESET_REBUILD_INCOMPLETE_CURRENT", {
              current: current.length,
              received: sorted.length,
              lastSeq,
            });
          } else {
            logEvent("gps", "ANDROID_NATIVE_RESET_DUPLICATE_SKIP", {
              current: current.length,
              received: sorted.length,
              lastSeq,
            });
            return;
          }
        }
        if (fresh.length > 0 && fresh.length <= 20 && currentLooksComplete) {
          nextPoints = fresh;
          forceIncrementalFromReset = true;
          logEvent("gps", "ANDROID_NATIVE_RESET_AS_INCREMENTAL", {
            current: current.length,
            received: sorted.length,
            fresh: fresh.length,
            lastSeq,
          });
        }
      }
      const currentLastTs = Number(current[current.length - 1]?.ts) || 0;
      const hasBackfill = nextPoints.some(
        (point) => currentLastTs && (Number(point?.ts) || 0) <= currentLastTs
      );
      const maxReceivedSeq = nextPoints.reduce(
        (maxSeq, point) =>
          typeof point?.seq === "number" ? Math.max(maxSeq, point.seq) : maxSeq,
        -1
      );
      const commitReceivedSequence = () => {
        if (maxReceivedSeq < 0) return;
        androidNativeSeqRef.current = Math.max(
          androidNativeSeqRef.current ?? -1,
          maxReceivedSeq
        );
        lastSeqRef.current = Math.max(lastSeqRef.current ?? -1, maxReceivedSeq);
      };
      const recentLargeBuffer = androidLastLargeBufferApplyRef.current || {};
      const recentRawPoints = Number(
        recentLargeBuffer.rawPoints || recentLargeBuffer.points || 0
      );
      const recentAcceptedPoints = Number(
        recentLargeBuffer.acceptedPoints || recentLargeBuffer.points || 0
      );
      const skipDuplicateReset =
        !!opts.reset &&
        recentLargeBuffer.ts &&
        Date.now() - recentLargeBuffer.ts < 5000 &&
        maxReceivedSeq >= 0 &&
        maxReceivedSeq <= recentLargeBuffer.seq &&
        nextPoints.length <=
          Math.max(
            ANDROID_BUFFER_DIRECT_APPLY_MIN,
            Math.floor(recentRawPoints * 1.25)
          ) &&
        current.length >= Math.max(2, Math.floor(recentAcceptedPoints * 0.8));

      if (skipDuplicateReset) {
        logEvent("gps", "ANDROID_NATIVE_POLL_SKIP_DUPLICATE_REBUILD", {
          current: current.length,
          received: nextPoints.length,
          maxReceivedSeq,
          largeBufferSeq: recentLargeBuffer.seq,
          largeBufferAcceptedPoints: recentAcceptedPoints,
          largeBufferRawPoints: recentRawPoints,
        });
        return;
      }

      const requiresRebuild =
        !!opts.forceRebuild ||
        (!!opts.reset && !forceIncrementalFromReset) ||
        !current.length ||
        (nextPoints.length > 50 && !opts.resumeIncremental) ||
        hasBackfill;
      let appliedPoints = nextPoints;

      if (requiresRebuild) {
        const acceptableNext = nextPoints.filter(isAcceptableRecordingAcc);
        const bulkMerge = nextPoints.length >= ANDROID_BULK_FILTER_LOG_MIN;
        const mergeStartedAt = Date.now();
        const rebuildSource = opts.reset
          ? acceptableNext
          : [...current, ...acceptableNext];
        const merged = mergeAndFilterAndroidPath(rebuildSource, {
          bulk: bulkMerge,
          source: opts.reason || "android_native_rebuild",
        });
        if (!merged.length) {
          commitReceivedSequence();
          return;
        }
        // Android resume: draw first, then update the full store. Updating the
        // store with hundreds of points can trigger React work before Leaflet
        // paints, which is visible as a slow resume.
        emitAndroidRender(sampleAndroidRenderPath(merged), true, {
          reason: opts.reason || "android_native_rebuild",
        });
        const deferRecordingPersist =
          isAndroidRef.current &&
          String(opts.reason || "").startsWith("resume");
        storeState.setPoints(merged, {
          persist: !deferRecordingPersist,
        });
        appliedPoints = merged;
        if (maxReceivedSeq >= 0 && nextPoints.length >= ANDROID_BUFFER_DIRECT_APPLY_MIN) {
          androidLastLargeBufferApplyRef.current = {
            ts: Date.now(),
            seq: maxReceivedSeq,
            points: merged.length,
            acceptedPoints: merged.length,
            rawPoints: nextPoints.length,
          };
        }
        logEvent("gps", "Android native points: chronological_rebuild", {
          current: current.length,
          received: nextPoints.length,
          merged: merged.length,
          sourcePoints: rebuildSource.length,
          authoritativeReset: !!opts.reset,
          mergeMs: Date.now() - mergeStartedAt,
          hasBackfill,
          reset: !!opts.reset,
          persistDeferred: deferRecordingPersist,
        });
      } else {
        const tail = [];
        let prev = current[current.length - 1] || null;
        for (const point of nextPoints) {
          if (!isAcceptableRecordingAcc(point)) continue;
          if (prev && !isReasonableStep(prev, point)) {
            logAndroidDrop("unreasonable_step_native", point, {
              prevTs: Number(prev?.ts) || null,
              distanceM: Math.round(haversine(prev, point)),
            });
            continue;
          }
          const pointWithGap = markGapIfNeeded(point, prev);
          tail.push(pointWithGap);
          prev = pointWithGap;
        }
        if (!tail.length) {
          commitReceivedSequence();
          return;
        }
        if (typeof storeState.appendPoints === "function") {
          storeState.appendPoints(tail, {
            persist: !opts.resumeIncremental,
          });
        } else {
          tail.forEach((p) => storeState.addPoint(p));
        }
        emitAndroidRender(tail, false, {
          reason: opts.reason || "android_native_append",
        });
        appliedPoints = tail;
      }

      const last = appliedPoints[appliedPoints.length - 1];
      commitReceivedSequence();
      if (last?.ts && last.ts > lastPointTsRef.current) {
        lastPointTsRef.current = last.ts;
      }
      androidNativeLastPointRef.current = last;
      androidPathActiveTsRef.current = Date.now();

      if (last && typeof last.lat === "number" && typeof last.lon === "number") {
        storeState.setUserPosition({
          lat: last.lat,
          lon: last.lon,
          ele: Number.isFinite(last.ele) ? last.ele : null,
          acc: Number.isFinite(last.acc) ? last.acc : null,
          spd: Number.isFinite(last.spd) ? last.spd : null,
          ts: last.ts || Date.now(),
        });
      }
    }

    function isAndroidOutOfOrder(nextTs, lastTs) {
      if (!isAndroidRef.current) return false;
      const ts = Number(nextTs) || 0;
      const last = Number(lastTs) || 0;
      return ts && last && ts < last;
    }

    function androidAcceptRecordingPoint(p) {
      if (!isAndroidRef.current || !androidRecordingWarmupRef.current) return true;

      const now = Date.now();
      const start = androidRecordingStartTsRef.current || now;
      if (now - start >= ANDROID_RECORDING_WARMUP_MAX_MS) {
        androidRecordingWarmupRef.current = false;
        androidRecordingWarmupOkTsRef.current = now;
        return true;
      }

      const acc = Number(p?.acc);
      if (Number.isFinite(acc) && acc <= ANDROID_RECORDING_WARMUP_ACC) {
        androidRecordingWarmupRef.current = false;
        androidRecordingWarmupOkTsRef.current = Number(p?.ts) || now;
        return true;
      }

      return false;
    }

    function filterAndroidPath(points) {
      if (!isAndroidRef.current) return points;
      const filtered = [];
      let lastTs = 0;
      for (const p of points) {
        const ts = Number(p?.ts) || 0;
        if (!ts) continue;
        if (lastTs && ts < lastTs) continue;
        lastTs = ts;
        filtered.push(p);
      }
      return filtered;
    }

    // Reorder buffered points by seq (fallback ts) and filter by distance threshold (meters).
    // Keep the best accuracy when points are within `distanceThreshold`.
    function reorderAndFilterPoints(points, distanceThreshold = 2, timeWindowMs = ANDROID_BUFFER_OOO_MS) {
      if (!Array.isArray(points) || !points.length) return points;
      // prefer seq when available
      const sorted = [...points].sort((a, b) => {
        const aSeq = typeof a?.seq === 'number' ? a.seq : null;
        const bSeq = typeof b?.seq === 'number' ? b.seq : null;
        if (aSeq != null && bSeq != null) return aSeq - bSeq;
        if (aSeq != null) return -1;
        if (bSeq != null) return 1;
        return (Number(a?.ts) || 0) - (Number(b?.ts) || 0);
      });

      const accepted = [];
      for (const p of sorted) {
        if (!accepted.length) {
          accepted.push(p);
          continue;
        }
        const last = accepted[accepted.length - 1];
        const dist = haversine(last, p);
        const timeDelta = Math.abs((Number(p?.ts) || 0) - (Number(last?.ts) || 0));
        // If points are temporally far, accept regardless of distance (avoid merging distinct segments)
        if (timeWindowMs && timeDelta > timeWindowMs) {
          accepted.push(markGapIfNeeded(p, last));
          continue;
        }
        if (dist >= distanceThreshold) {
          accepted.push(markGapIfNeeded(p, last));
          continue;
        }
        // within distance threshold -> keep the one with better accuracy
        const accLast = Number(last?.acc) || 99999;
        const accP = Number(p?.acc) || 99999;
        if (accP < accLast) {
          accepted[accepted.length - 1] = p; // replace with better accuracy
        }
        // otherwise drop p
      }
      return accepted;
    }

    function markGapIfNeeded(nextPoint, lastPoint) {
      if (!nextPoint || !lastPoint) return nextPoint;
      const isRecordingNow = !!usePanelsStore.getState()?.isRecording;
      const nextTs = Number(nextPoint.ts) || 0;
      const lastTs = Number(lastPoint.ts) || 0;
      const dt = nextTs && lastTs ? nextTs - lastTs : 0;
      const dist = haversine(lastPoint, nextPoint);
      if (isAndroidRef.current && androidForceGapRef.current) {
        androidForceGapRef.current = false;
        return { ...nextPoint, gap: true };
      }
      if (dt <= 0) return nextPoint;
      const shouldMarkGap =
        isAndroidRef.current && isRecordingNow
          ? shouldMarkAndroidRecordingGap(lastPoint, nextPoint, {
              minGapMs: GAP_SECONDS * 1000,
              minDistanceM: Math.max(200, GAP_DISTANCE_METERS),
            })
          : dt >= GAP_SECONDS * 1000 ||
            (dt >= 30000 && dist >= GAP_DISTANCE_METERS);
      if (shouldMarkGap) {
        if (isRecordingNow && isAndroidRef.current) {
          const now = Date.now();
          const lastLog = androidRecordingGapLogRef.current || { ts: 0, count: 0 };
          lastLog.count = Number(lastLog.count || 0) + 1;
          if (now - Number(lastLog.ts || 0) > 15000) {
            logEvent("gps", "Android recording gap", {
              distMeters: Math.round(dist),
              dtSec: Math.round(dt / 1000),
              suppressed: Math.max(0, lastLog.count - 1),
            });
            androidRecordingGapLogRef.current = { ts: now, count: 0 };
          } else {
            androidRecordingGapLogRef.current = lastLog;
          }
          return { ...nextPoint, gap: true };
        }
        return { ...nextPoint, gap: true };
      }
      return nextPoint;
    }

    function shouldSkipResumeAnomaly(prev, next) {
      if (!isAndroidRef.current) return false;
      const isRecordingNow = !!usePanelsStore.getState()?.isRecording;
      if (isRecordingNow) return false;
      if (androidResumeSkipLeftRef.current <= 0) return false;
      if (!prev || !next) return false;
      const dist = haversine(prev, next);
      if (dist < ANDROID_RESUME_SKIP_DISTANCE_METERS) return false;

      const prevTs = Number(prev?.ts) || 0;
      const nextTs = Number(next?.ts) || 0;
      const dtSec = prevTs && nextTs ? (nextTs - prevTs) / 1000 : 0;
      const speedMps =
        dtSec > 0 ? Number((dist / dtSec).toFixed(2)) : null;

      if (Number.isFinite(speedMps) && speedMps >= ANDROID_RESUME_SKIP_SPEED_MPS) {
        // Do not drop the point — just mark a visual gap to avoid long lines.
        androidResumeSkipLeftRef.current = 0;
        androidForceGapRef.current = true;
        logEvent("gps", "Android resume anomaly gap", {
          distMeters: Math.round(dist),
          dtSec: Math.round(dtSec),
          speedMps,
        });
        return false;
      }

      if (dtSec >= ANDROID_RESUME_GAP_MIN_SECONDS) {
        androidForceGapRef.current = true;
        androidResumeSkipLeftRef.current = 0;
        logEvent("gps", "Android resume gap", {
          distMeters: Math.round(dist),
          dtSec: Math.round(dtSec),
          speedMps,
        });
      }
      return false;
    }

    function onGpsUpdate(ev) {
      if (!mountedRef.current) return;

      const d = ev?.detail;
      if (!d) return;

      const fix = extractFix(d);

      const storeState = usePanelsStore.getState();
      const {
        isRecording,
        isRecordingPaused,
        recordingStartTime,
        setUserPosition,
        addPoint,
        sendLiveUpdate,
        points,
        setPoints,
      } = storeState;
      const isRecordingActive = !!isRecording && !isRecordingPaused;
      const androidNativeRecording =
        isAndroidRef.current && isRecordingActive;

      const pathArr = Array.isArray(d.path) ? d.path : null;
      const storePoints = Array.isArray(points) ? points : [];
      const recordStart = Number(recordingStartTime) || null;
      const filteredPath = androidNativeRecording
        ? null
        : pathArr
        ? pathArr.filter((p) => {
            if (!recordStart) return true;
            const ts = Number(p?.ts || p?.time || 0);
            return ts && ts >= recordStart - 2000;
          })
        : null;
      if (isRecordingActive && filteredPath && filteredPath.length) {
        androidPathActiveTsRef.current = Date.now();
      }

      const normalizeTs = (val) => {
        const n = Number(val) || 0;
        if (!n) return 0;
        return n < 1e12 ? n * 1000 : n;
      };
      const fixSrc = fix?.__src || fix?.src || null;
      const isFixBufferLike = fixSrc === "android-buffer" || fixSrc === "android-bg";
      const fixSeq = typeof fix?.seq === "number" ? fix.seq : null;
      const fixTs = normalizeTs(fix?.ts || fix?.time || 0);
      const storePosTs = normalizeTs(storeState?.userPosition?.ts || storeState?.userPosition?.time || 0);
      const isFixStaleByTs =
        isAndroidRef.current && !!fixTs && !!storePosTs && fixTs < storePosTs;
      const isFixStaleBySeq =
        isAndroidRef.current &&
        !isFixBufferLike &&
        fixSeq != null &&
        lastSeqRef.current >= 0 &&
        fixSeq < lastSeqRef.current;
      const shouldUseFixForRealtime =
        !!(fix && typeof fix.lat === "number" && typeof fix.lon === "number") &&
        !(isFixStaleByTs || isFixStaleBySeq);
      const effectiveRealtimeFix = shouldUseFixForRealtime
        ? fix
        : storeState?.userPosition && typeof storeState.userPosition.lat === "number" &&
          typeof storeState.userPosition.lon === "number"
        ? storeState.userPosition
        : null;

      // ------------------------------
      // 1️⃣ Atualizar posição atual
      // ------------------------------
      if (shouldUseFixForRealtime) {
        lastGpsUpdateTsRef.current = Date.now();
        const tsFix = fix.ts || fix.time || Date.now();

        setUserPosition({
          lat: fix.lat,
          lon: fix.lon,
          ele: Number.isFinite(fix.alt ?? fix.ele) ? (fix.alt ?? fix.ele) : null,
          acc: Number.isFinite(fix.acc) ? fix.acc : null,
          spd: Number.isFinite(Number(fix.spd ?? fix.speed))
            ? Number(fix.spd ?? fix.speed)
            : null,
          ts: tsFix,
        });
        liveLog("gps:update", {
          lat: fix.lat,
          lon: fix.lon,
          acc: fix.acc,
          ts: tsFix,
          isRecording,
        });
        syncLiveMemberLocation(fix);
      } else if (isAndroidRef.current && fix && (isFixStaleByTs || isFixStaleBySeq)) {
        logEvent("gps", "ANDROID_FIX_SKIPPED_REALTIME", {
          reason: isFixStaleByTs ? "stale_ts" : "stale_seq",
          src: fixSrc || null,
          fixTs: fixTs || null,
          storePosTs: storePosTs || null,
          fixSeq,
          lastSeq: lastSeqRef.current ?? null,
        });
      }

      // ------------------------------
      // 2️⃣ Registar ponto (GPS local)
      // ------------------------------
      let pathPoint = null;

      if (androidNativeRecording && androidResumeSyncInFlightRef.current) {
        // While rebuilding after resume, keep updating the position marker but
        // do not connect live fixes to an incomplete recorded path.
        pathPoint = androidNativeLastPointRef.current;
      } else if (androidNativeRecording) {
        const src = fix?.__src || fix?.src || null;
        const isBufferSrc = src === "android-buffer" || src === "android-bg";
        if (!isBufferSrc && fix && typeof fix.lat === "number" && typeof fix.lon === "number") {
          const next = toPoint(fix);
          if (next) {
            const seq = typeof next.seq === "number" ? next.seq : null;
            const isDupSeq = seq !== null && seq <= lastSeqRef.current;

            if (!isDupSeq) {
              const allowRecordPoint = androidAcceptRecordingPoint(next);
              const allowAcc = isAcceptableRecordingAcc(next);

              if (recordStart && next.ts && next.ts < recordStart - 2000) {
                // ignore old point
              } else if (!allowRecordPoint || !allowAcc) {
                logAndroidDrop(!allowRecordPoint ? "warmup" : "acc", next);
              } else if (!isAndroidBufferLike(next) && isAndroidOutOfOrder(next.ts, lastPointTsRef.current)) {
                logAndroidDrop("out_of_order", next, { lastTs: lastPointTsRef.current });
              } else if (!isAndroidBufferLike(next) && !shouldAcceptByTime(next.ts)) {
                logAndroidDrop("min_interval", next);
              } else {
                const lastStored = storePoints[storePoints.length - 1] || null;
                if (shouldSkipResumeAnomaly(lastStored, next)) {
                  logAndroidDrop("resume_anomaly", next);
                } else if (!isReasonableStep(lastStored, next)) {
                  logAndroidDrop("unreasonable_step", next);
                } else {
                  pathPoint = markGapIfNeeded(next, lastStored);
                  addPoint(pathPoint);
                  if (isAndroidRef.current) {
                    emitAndroidRender([pathPoint]);
                  }
                  androidNativeLastPointRef.current = pathPoint;
                  if (seq != null) {
                    androidNativeSeqRef.current = seq;
                    lastSeqRef.current = seq;
                    if (seq > (lastBufferSeqRef.current ?? -1)) {
                      lastBufferSeqRef.current = seq;
                    }
                  }
                  if (pathPoint?.ts && pathPoint.ts > lastPointTsRef.current) {
                    lastPointTsRef.current = pathPoint.ts;
                  }
                }
              }
            }
          }
        }

        if (!pathPoint) pathPoint = androidNativeLastPointRef.current;
      } else if (isRecordingActive && fix && typeof fix.lat === "number" && typeof fix.lon === "number") {
        const allowRecordPoint = androidAcceptRecordingPoint(fix);
        const allowAcc = isAcceptableRecordingAcc(fix);
        const seq = typeof fix.seq === "number" ? fix.seq : null;

        // 🧠 Ignorar APENAS duplicados imediatos (não bloquear buffer)
        const isDupSeq = seq !== null && seq === lastSeqRef.current;
        if (isDupSeq) {
          const now = Date.now();
          if (!dupSeqWarnRef.current.ts || now - dupSeqWarnRef.current.ts > 2000) {
            dupSeqWarnRef.current = { ts: now, count: 1 };
            console.log("⏭️ Ignorado duplicado:", seq);
          } else {
            dupSeqWarnRef.current.count += 1;
            if (dupSeqWarnRef.current.count % 25 === 0) {
              console.log("⏭️ Ignorado duplicado (x" + dupSeqWarnRef.current.count + ")");
            }
          }
        }

        if (recordStart && (fix.ts || fix.time)) {
          const tsFix = Number(fix.ts || fix.time || 0);
          if (tsFix && tsFix < recordStart - 2000) return;
        }

        if (!allowRecordPoint || !allowAcc) {
          pathPoint = null;
          logAndroidDrop(!allowRecordPoint ? "warmup" : "acc", fix);
        } else if (isAndroidRef.current && filteredPath && filteredPath.length) {
          const missing = filteredPath.slice(storePoints.length);
          const shouldSync =
            (forceSyncRef.current && storePoints.length === 0) || missing.length >= 120;

          if (shouldSync) {
            const okTs = androidRecordingWarmupOkTsRef.current;
            const normalized = filterAndroidPath(
              filteredPath.map(toPoint).filter(Boolean)
            )
              .filter(isAcceptableRecordingAcc)
              .filter((p) => !okTs || (Number(p?.ts) || 0) >= okTs);

            if (
              isAndroidRef.current &&
              androidForceGapRef.current &&
              normalized.length &&
              storePoints.length &&
              normalized.length > storePoints.length
            ) {
              const idx = storePoints.length;
              const target = normalized[idx];
              if (target) normalized[idx] = { ...target, gap: true };
              androidForceGapRef.current = false;
            }
            if (normalized.length && normalized.length >= storePoints.length) {
              console.log("🧱 Sync path →", {
                missing: missing.length,
                total: filteredPath.length,
              });
              setPoints(normalized);
              pathPoint = normalized[normalized.length - 1] || null;
              if (isAndroidRef.current) {
                emitAndroidRender(sampleAndroidRenderPath(normalized), true);
              }
              forceSyncRef.current = false;
              lastPathSyncTsRef.current = Date.now();
            }
          } else {
            if (missing.length) {
              console.log("🧱 Backfill path →", {
                missing: missing.length,
                total: filteredPath.length,
              });
            }
            let lastTs = lastPointTsRef.current || storePoints[storePoints.length - 1]?.ts || 0;
            const firstMissingTs = Number(missing[0]?.ts || missing[0]?.time || 0);
            if (lastTs && firstMissingTs && firstMissingTs < lastTs - 2000) {
              // Merge out-of-order missing points instead of dropping.
              const merged = filterAndroidPath(
                [...storePoints, ...missing.map(toPoint).filter(Boolean)]
              )
                .filter(isAcceptableRecordingAcc)
                .sort((a, b) => (Number(a?.ts) || 0) - (Number(b?.ts) || 0));

              if (merged.length) {
                setPoints(merged);
                const lastMergedTs = Number(merged[merged.length - 1]?.ts) || 0;
                if (lastMergedTs > lastPointTsRef.current) lastPointTsRef.current = lastMergedTs;
                pathPoint = merged[merged.length - 1] || null;
                if (isAndroidRef.current) {
                  emitAndroidRender(sampleAndroidRenderPath(merged), true);
                }
                lastPathSyncTsRef.current = Date.now();
                return;
              }
            }
            for (const p of missing) {
              const next = toPoint(p);
              if (!next) continue;
              if (!androidAcceptRecordingPoint(next)) {
                logAndroidDrop("warmup", next);
                continue;
              }
              if (!isAcceptableRecordingAcc(next)) {
                logAndroidDrop("acc", next);
                continue;
              }
              const okTs = androidRecordingWarmupOkTsRef.current;
              if (okTs && (Number(next.ts) || 0) < okTs) continue;
              if (isAndroidOutOfOrder(next.ts, lastTs)) {
                logAndroidDrop("out_of_order", next, { lastTs });
                continue;
              }
              if (isAndroidBufferLike(next)) {
                const ts = Number(next.ts) || Date.now();
                if (ts < lastPointTsRef.current) continue;
                if (ts > lastPointTsRef.current) lastPointTsRef.current = ts;
              } else if (!shouldAcceptByTime(next.ts)) {
                logAndroidDrop("min_interval", next);
                continue;
              }
              const lastStored = storePoints[storePoints.length - 1] || null;
              if (shouldSkipResumeAnomaly(lastStored, next)) {
                logAndroidDrop("resume_anomaly", next);
                continue;
              }
              if (!isReasonableStep(lastStored, next)) {
                logAndroidDrop("unreasonable_step", next);
                continue;
              }
              pathPoint = markGapIfNeeded(next, lastStored);
              addPoint(pathPoint);
              if (pathPoint && isAndroidRef.current) {
                emitAndroidRender([pathPoint]);
              }
              lastTs = Number(pathPoint?.ts) || lastTs;
            }
            if (missing.length) lastPathSyncTsRef.current = Date.now();
          }
        } else if (!isDupSeq) {
          pathPoint = toPoint(fix);
          if (pathPoint) {
            if (!androidAcceptRecordingPoint(pathPoint)) {
              logEvent("gps", "ANDROID_POINT_DROP", {
                reason: "warmup",
                acc: Number.isFinite(pathPoint?.acc)
                  ? Math.round(pathPoint.acc)
                  : null,
                ts: pathPoint?.ts || null,
                src: pathPoint?.src || null,
              });
              pathPoint = null;
            }
            if (pathPoint && !isAcceptableRecordingAcc(pathPoint)) {
              logEvent("gps", "ANDROID_POINT_DROP", {
                reason: "acc",
                acc: Number.isFinite(pathPoint?.acc)
                  ? Math.round(pathPoint.acc)
                  : null,
                ts: pathPoint?.ts || null,
                src: pathPoint?.src || null,
              });
              pathPoint = null;
            }
            const okTs = androidRecordingWarmupOkTsRef.current;
            if (pathPoint && okTs && (Number(pathPoint.ts) || 0) < okTs) {
              pathPoint = null;
            }
            if (pathPoint) {
              if (isAndroidBufferLike(pathPoint)) {
                const ts = Number(pathPoint.ts) || Date.now();
                if (ts < lastPointTsRef.current) {
                  logAndroidDrop("buffer_old", pathPoint, { lastTs: lastPointTsRef.current });
                  pathPoint = null;
                } else if (ts > lastPointTsRef.current) {
                  lastPointTsRef.current = ts;
                }
              } else if (!shouldAcceptByTime(pathPoint.ts)) {
                logEvent("gps", "ANDROID_POINT_DROP", {
                  reason: "min_interval",
                  ts: pathPoint?.ts || null,
                  src: pathPoint?.src || null,
                });
                pathPoint = null;
              }
            }
          }
          if (pathPoint) {
            const lastStored = storePoints[storePoints.length - 1] || null;
            if (isAndroidOutOfOrder(pathPoint.ts, lastStored?.ts)) {
              logEvent("gps", "ANDROID_POINT_DROP", {
                reason: "out_of_order",
                ts: pathPoint?.ts || null,
                lastTs: lastStored?.ts || null,
                src: pathPoint?.src || null,
              });
              pathPoint = null;
            }
          }
          if (pathPoint) {
            const lastStored = storePoints[storePoints.length - 1] || null;
            if (shouldSkipResumeAnomaly(lastStored, pathPoint)) {
              logEvent("gps", "ANDROID_POINT_DROP", {
                reason: "resume_anomaly",
                ts: pathPoint?.ts || null,
                src: pathPoint?.src || null,
              });
              pathPoint = null;
            }
          }
          if (pathPoint) {
            const lastStored = storePoints[storePoints.length - 1] || null;
            if (!isReasonableStep(lastStored, pathPoint)) {
              logEvent("gps", "ANDROID_POINT_DROP", {
                reason: "unreasonable_step",
                ts: pathPoint?.ts || null,
                src: pathPoint?.src || null,
              });
              pathPoint = null;
            }
          }
          if (pathPoint) {
            const lastStored = storePoints[storePoints.length - 1] || null;
            pathPoint = markGapIfNeeded(pathPoint, lastStored);
            addPoint(pathPoint);
            if (isAndroidRef.current) {
              emitAndroidRender([pathPoint]);
            }
          }
        }

        if (seq !== null) {
          lastSeqRef.current = seq;
        }
      }


      // ------------------------------
      // 3️⃣ Pegar métricas REAIS do Zustand
      // ------------------------------
      const st = usePanelsStore.getState();

      const payload = {
        type: "gps",
        ts: Date.now(),

        pos: effectiveRealtimeFix
          ? {
              lat: effectiveRealtimeFix.lat,
              lon: effectiveRealtimeFix.lon,
              ele: Number.isFinite(effectiveRealtimeFix.alt ?? effectiveRealtimeFix.ele)
                ? (effectiveRealtimeFix.alt ?? effectiveRealtimeFix.ele)
                : null,
              acc: Number.isFinite(effectiveRealtimeFix.acc)
                ? effectiveRealtimeFix.acc
                : null,
              spd: Number.isFinite(
                Number(effectiveRealtimeFix.spd ?? effectiveRealtimeFix.speed)
              )
                ? Number(effectiveRealtimeFix.spd ?? effectiveRealtimeFix.speed)
                : null,
              ts: effectiveRealtimeFix.ts || effectiveRealtimeFix.time || Date.now(),
            }
          : null,

        // 🔥 MÉTRICAS reais
        stats: {
          distanceKm: st.totalDistance / 1000,
          movingTime: st.movingTime,
          stoppedTime: st.stoppedTime,
          currentSpeed: st.currentSpeed,
          elevationGain: st.elevationGain,
          elevationLoss: st.elevationLoss,
        },

        // 🔥 PATHPOINT incluído — Viewer passa a ter gráfico!
        pathPoint,
      };

      // ------------------------------
      // 4️⃣ Enviar Live Update
      // ------------------------------
      try {
        sendLiveUpdate(payload);
      } catch (err) {
        console.warn("⚠️ Erro ao enviar LiveUpdate:", err);
      }
    }

    window.addEventListener("gps:update", onGpsUpdate);
      const drainPendingBuffer = (limit) => {
        const {
          isRecording,
          isRecordingPaused,
          recordingFinalizeInProgress,
          addPoint,
          points,
          setPoints,
        } = usePanelsStore.getState();
        if (!isRecording || isRecordingPaused) return;
        if (recordingFinalizeInProgress) return;
        if (androidResumeSyncInFlightRef.current) return;
        const minTs = getRecordingMinTs();
        const queue = pendingBufferRef.current;
        if (!queue.length) return;

      const slice = Number.isFinite(limit) ? queue.splice(0, limit) : queue.splice(0);
      const current = Array.isArray(points) ? points : [];
      // Ordenar buffer por seq (preferência) ou ts para evitar out_of_order
      const sliceSorted = [...slice].sort((a, b) => {
        const aSeq = typeof a?.seq === "number" ? a.seq : null;
        const bSeq = typeof b?.seq === "number" ? b.seq : null;
        if (aSeq != null && bSeq != null) return aSeq - bSeq;
        const aTs = Number(a?.ts || a?.time || 0);
        const bTs = Number(b?.ts || b?.time || 0);
        return aTs - bTs;
      });
      const mappedSlice = sliceSorted.map(toPoint).filter(Boolean);
      // Preprocess buffered batches: reorder by seq and filter close points (<2m)
      let processedSlice = mappedSlice;
      try {
        const isBufferBatch = mappedSlice.some((p) => p?.src === "android-buffer" || p?.src === "android-bg");
        if (isBufferBatch) {
          const before = mappedSlice.length;
          processedSlice = reorderAndFilterPoints(mappedSlice, 2, ANDROID_BUFFER_OOO_MS);
          logEvent("gps", "Android drainPendingBuffer: reorder_filter", {
            raw: before,
            after: processedSlice.length,
            first: processedSlice[0] ? { seq: processedSlice[0].seq, lat: processedSlice[0].lat, lon: processedSlice[0].lon } : null,
            last: processedSlice[processedSlice.length - 1] ? { seq: processedSlice[processedSlice.length - 1].seq, lat: processedSlice[processedSlice.length - 1].lat, lon: processedSlice[processedSlice.length - 1].lon } : null,
          });
        }
      } catch (e) {}

      const hasOlderBufferPoint = processedSlice.some(
        (p) =>
          (p?.src === "android-buffer" || p?.src === "android-bg") &&
          (Number(p?.ts) || 0) < (lastPointTsRef.current || 0)
      );

      if (hasOlderBufferPoint) {
        const mergeSource = [
          ...current,
          ...processedSlice.filter(isAcceptableRecordingAcc),
        ];
        const mergedBase = mergeAndFilterAndroidPath(mergeSource, {
          bulk: mergeSource.length >= ANDROID_BULK_FILTER_LOG_MIN,
          source: "pending_buffer_backfill",
        }).filter((p) => !minTs || (Number(p.ts) || 0) >= minTs);
        const merged = mergedBase.map((p, i) => {
          if (i === 0) return p;
          return markGapIfNeeded(p, mergedBase[i - 1]);
        });

        if (merged.length) {
          setPoints(merged);
          const lastMergedTs = Number(merged[merged.length - 1]?.ts) || 0;
          if (lastMergedTs > lastPointTsRef.current) lastPointTsRef.current = lastMergedTs;
          if (isAndroidRef.current) {
            emitAndroidRender(sampleAndroidRenderPath(merged), true);
          }
          return;
        }
      }

      // Processar atomically: não actualizar lastBufferSeqRef/lastPointTsRef durante a validação
      const candidates = [];
      let droppedCount = 0;
      const droppedSamples = [];
      let localLastSeq = Math.max(
        lastBufferSeqRef.current ?? -1,
        lastSeqRef.current ?? -1,
        androidNativeSeqRef.current ?? -1
      );
      let gapPrev = current[current.length - 1] || null;

      for (const p of sliceSorted) {
        const seq = typeof p?.seq === "number" ? p.seq : null;
        const isBuffer = p?.fromBuffer || p?.src === "android-buffer" || p?.src === "android-bg";
        if (isDuplicateAndroidPoint(p)) {
          droppedCount++;
          logAndroidDrop("point_duplicate", p);
          if (droppedSamples.length < 3) {
            droppedSamples.push({
              reason: "point_duplicate",
              lat: p?.lat ?? null,
              lon: p?.lon ?? null,
              seq,
              ts: p?.ts ?? null,
              src: p?.src ?? null,
            });
          }
          continue;
        }
        // relax seq duplicates for buffer points: accept if timestamp is newer than lastPointTs
        if (seq != null && seq <= localLastSeq) {
          if (isBuffer && p?.ts && Number(p.ts) > (lastPointTsRef.current || 0)) {
            const originalSeq = seq;
            p.seq = localLastSeq + 1;
            logEvent("gps", "Android drainPendingBuffer: seq_relaxed", {
              reason: "seq_duplicate_relaxed",
              originalSeq,
              newSeq: p.seq,
              ts: p.ts,
              lastSeq: localLastSeq,
            });
            // proceed to accept this promoted point
          } else {
            droppedCount++;
            logAndroidDrop("seq_duplicate", p, { lastSeq: localLastSeq });
            if (droppedSamples.length < 3) droppedSamples.push({ reason: "seq_duplicate", lat: p?.lat ?? null, lon: p?.lon ?? null, seq, ts: p?.ts ?? null, src: p?.src ?? null });
            continue;
          }
        }

        const next = toPoint(p);
        if (!next) continue;
        if (minTs && (Number(next.ts) || 0) < minTs) {
          droppedCount++;
          if (droppedSamples.length < 3) droppedSamples.push({ reason: "minTs", lat: next?.lat ?? null, lon: next?.lon ?? null, seq: typeof next?.seq === "number" ? next.seq : null, ts: next?.ts ?? null, src: next?.src ?? null });
          continue;
        }

        if (!isAcceptableRecordingAcc(next)) {
          logAndroidDrop("acc", next);
          droppedCount++;
          if (droppedSamples.length < 3) droppedSamples.push({ reason: "acc", lat: next?.lat ?? null, lon: next?.lon ?? null, seq: typeof next?.seq === "number" ? next.seq : null, ts: next?.ts ?? null, acc: Number.isFinite(next?.acc) ? Math.round(next.acc) : null, src: next?.src ?? null });
          continue;
        }

        // Não forçar 'out_of_order' para pontos buffer-like — deixamos a ordenação por seq cuidar disso
        if (!isAndroidBufferLike(next) && isAndroidOutOfOrder(next.ts, lastPointTsRef.current)) {
          logAndroidDrop("out_of_order", next, { lastTs: lastPointTsRef.current });
          droppedCount++;
          if (droppedSamples.length < 3) droppedSamples.push({ reason: "out_of_order", lat: next?.lat ?? null, lon: next?.lon ?? null, seq: typeof next?.seq === "number" ? next.seq : null, ts: next?.ts ?? null, lastTs: lastPointTsRef.current, src: next?.src ?? null });
          continue;
        }

        if (!isAndroidBufferLike(next) && !shouldAcceptByTime(next.ts)) {
          logAndroidDrop("min_interval", next);
          droppedCount++;
          if (droppedSamples.length < 3) droppedSamples.push({ reason: "min_interval", lat: next?.lat ?? null, lon: next?.lon ?? null, seq: typeof next?.seq === "number" ? next.seq : null, ts: next?.ts ?? null, src: next?.src ?? null });
          continue;
        }

        const prevForCheck = candidates.length ? candidates[candidates.length - 1] : current[current.length - 1] || null;
        if (shouldSkipResumeAnomaly(prevForCheck, next)) {
          logAndroidDrop("resume_anomaly", next);
          droppedCount++;
          if (droppedSamples.length < 3) droppedSamples.push({ reason: "resume_anomaly", lat: next?.lat ?? null, lon: next?.lon ?? null, seq: typeof next?.seq === "number" ? next.seq : null, ts: next?.ts ?? null, src: next?.src ?? null });
          continue;
        }

        if (!isReasonableStep(prevForCheck, next)) {
          logAndroidDrop("unreasonable_step", next);
          droppedCount++;
          if (droppedSamples.length < 3) droppedSamples.push({ reason: "unreasonable_step", lat: next?.lat ?? null, lon: next?.lon ?? null, seq: typeof next?.seq === "number" ? next.seq : null, ts: next?.ts ?? null, src: next?.src ?? null });
          continue;
        }

        candidates.push(smoothAndroidElevationPoint(next));
        if (seq != null && seq > localLastSeq) localLastSeq = seq;
      }

      if (!candidates.length) {
        logEvent("gps", "Android drainPendingBuffer: all_dropped", {
          raw: slice.length,
          accepted: 0,
          dropped: droppedCount,
          samplesDropped: droppedSamples,
          lastSeq: lastBufferSeqRef.current,
        });
        return;
      }

      // Aplicar marcações de gap e anexar ao store em bloco
      const tailWithGaps = candidates.map((p, i) => {
        const prev = i === 0 ? current[current.length - 1] || null : candidates[i - 1];
        return markGapIfNeeded(p, prev || current[current.length - 1]);
      });

      if (tailWithGaps.length > 20) {
        const mergedAll = [...current, ...tailWithGaps];
        setPoints(mergedAll);
        if (isAndroidRef.current) {
          emitAndroidRender(sampleAndroidRenderPath(mergedAll), true);
        }
      } else {
        if (typeof usePanelsStore.getState().appendPoints === "function") {
          usePanelsStore.getState().appendPoints(tailWithGaps);
        } else {
          tailWithGaps.forEach((p) => addPoint(p));
        }
        if (isAndroidRef.current) {
          emitAndroidRender(tailWithGaps);
        }
      }

      // Atualizar estados globais só depois de aplicar
      const lastTailTs = Number(tailWithGaps[tailWithGaps.length - 1]?.ts) || 0;
      if (lastTailTs > lastPointTsRef.current) lastPointTsRef.current = lastTailTs;
      if (localLastSeq > (lastBufferSeqRef.current ?? -1)) lastBufferSeqRef.current = localLastSeq;
      androidPathActiveTsRef.current = Date.now();

      const lastTail = tailWithGaps[tailWithGaps.length - 1];
      if (lastTail && typeof lastTail.seq === "number") {
        if (lastTail.seq > (androidNativeSeqRef.current ?? -1)) {
          androidNativeSeqRef.current = lastTail.seq;
        }
        if (lastTail.seq > (lastSeqRef.current ?? -1)) {
          lastSeqRef.current = lastTail.seq;
        }
      }
      if (lastTail && typeof lastTail.lat === "number" && typeof lastTail.lon === "number") {
        usePanelsStore.getState().setUserPosition({
          lat: lastTail.lat,
          lon: lastTail.lon,
          ele: Number.isFinite(lastTail.ele) ? lastTail.ele : null,
          acc: Number.isFinite(lastTail.acc) ? lastTail.acc : null,
          spd: Number.isFinite(lastTail.spd) ? lastTail.spd : null,
          ts: lastTail.ts || Date.now(),
        });
      }

      const samplesAccepted = candidates.slice(0, 3).map((p) => ({ lat: p.lat, lon: p.lon, seq: typeof p.seq === "number" ? p.seq : null, ts: p.ts || null, src: p.src || null }));
      logEvent("gps", "Android drainPendingBuffer: added_tail", {
        raw: slice.length,
        accepted: candidates.length,
        dropped: droppedCount,
        lastSeq: lastBufferSeqRef.current,
        samplesAccepted,
        samplesDropped: droppedSamples,
      });
    };

    const getDrainLimit = () => {
      const isVisible =
        typeof document !== "undefined" &&
        document.visibilityState === "visible";
      const isActive = appActiveRef.current || isVisible;
      return isActive ? BUFFER_DRAIN_MAX_POINTS_FG : BUFFER_DRAIN_MAX_POINTS_BG;
    };

    const scheduleBufferDrain = () => {
      if (bufferDrainTimerRef.current) return;
      bufferDrainTimerRef.current = setInterval(() => {
        if (!pendingBufferRef.current.length) {
          clearInterval(bufferDrainTimerRef.current);
          bufferDrainTimerRef.current = null;
          return;
        }
        drainPendingBuffer(getDrainLimit());
      }, BUFFER_DRAIN_INTERVAL_MS);
    };

    const consumeBuffer = (list) => {
      if (!Array.isArray(list) || list.length === 0) return;
      const storeState = usePanelsStore.getState();
      if (!storeState?.isRecording || storeState?.isRecordingPaused) {
        pendingBufferRef.current = [];
        logEvent("gps", "ANDROID_BUFFER_DROP_NOT_RECORDING", {
          count: list.length,
          isRecording: !!storeState?.isRecording,
          isRecordingPaused: !!storeState?.isRecordingPaused,
          reason: "consume_buffer_guard",
        });
        return;
      }
      if (isAndroidRef.current && list.length >= ANDROID_BUFFER_DIRECT_APPLY_MIN) {
        pendingBufferRef.current = [];
        consumeAndroidNativePoints(list, { reason: "buffer_direct_apply" });
        const appliedCount = Array.isArray(usePanelsStore.getState()?.points)
          ? usePanelsStore.getState().points.length
          : 0;
        const maxSeq = list.reduce((max, point) => {
          const seq = Number(point?.seq);
          return Number.isFinite(seq) ? Math.max(max, seq) : max;
        }, -1);
        if (maxSeq >= 0) {
          androidLastLargeBufferApplyRef.current = {
            ts: Date.now(),
            seq: maxSeq,
            points: appliedCount,
            acceptedPoints: appliedCount,
            rawPoints: list.length,
          };
        }
        logEvent("gps", "ANDROID_BUFFER_DIRECT_APPLY", {
          count: list.length,
          maxSeq,
          threshold: ANDROID_BUFFER_DIRECT_APPLY_MIN,
        });
        return;
      }
      pendingBufferRef.current.push(...list);
      scheduleBufferDrain();
    };

    const onGpsBuffer = (ev) => {
      if (!mountedRef.current || !isAndroidRef.current) return;
      const now = Date.now();
      const isRecentResume =
        androidResumeTsRef.current && now - androidResumeTsRef.current < 5000;
      const storeState = usePanelsStore.getState();
      if (!storeState) return;
      const isRecording = !!storeState.isRecording && !storeState?.isRecordingPaused;
      const recentFix =
        lastGpsUpdateTsRef.current &&
        now - lastGpsUpdateTsRef.current < 4000;
      const points = Array.isArray(ev?.detail?.points) ? ev.detail.points : [];
      if (!points.length) return;
      const { recordingStartTime } = storeState;
      let filteredPoints = points;
      if (isRecording && points.length) {
        const recordStart = Number(recordingStartTime) || 0;
        const minTs = Math.max(
          recordStart ? recordStart - 2000 : 0,
          androidResumeFenceTsRef.current || lastPointTsRef.current || 0
        );
        filteredPoints = points.filter((p) => {
          const ts = Number(p?.ts || p?.time || 0);
          return ts && (!minTs || ts >= minTs);
        });
      }
      if (!filteredPoints.length) return;

      const nativeSeq = androidNativeSeqRef.current ?? -1;
      const maxBufferSeq = filteredPoints.reduce((max, point) => {
        const seq = Number(point?.seq);
        return Number.isFinite(seq) ? Math.max(max, seq) : max;
      }, -1);
      if (nativeSeq >= 0 && maxBufferSeq >= 0 && maxBufferSeq <= nativeSeq) {
        pendingBufferRef.current = [];
        if (now - androidLastDuplicateBufferLogTsRef.current >= 5000) {
          androidLastDuplicateBufferLogTsRef.current = now;
          logEvent("gps", "ANDROID_BUFFER_DUPLICATE_SKIP", {
            count: points.length,
            filteredCount: filteredPoints.length,
            nativeSeq,
            maxBufferSeq,
          });
        }
        return;
      }

      if (
        isRecentResume &&
        isRecording &&
        points.length >= ANDROID_BUFFER_DIRECT_APPLY_MIN &&
        filteredPoints.length < Math.floor(points.length * 0.75)
      ) {
        pendingBufferRef.current = [];
        logEvent("gps", "ANDROID_BUFFER_PARTIAL_RESUME_SKIP", {
          count: points.length,
          filteredCount: filteredPoints.length,
          syncInFlight: !!androidResumeSyncInFlightRef.current,
          pollBusy: !!androidNativePollBusyRef.current,
        });
        if (
          androidNativePollNowRef.current &&
          !androidResumeSyncInFlightRef.current &&
          !androidNativePollBusyRef.current
        ) {
          const canResumeIncremental =
            (androidNativeSeqRef.current ?? -1) >= 0 &&
            Array.isArray(storeState.points) &&
            storeState.points.length >= ANDROID_RESUME_INCREMENTAL_MIN_POINTS &&
            lastPointTsRef.current &&
            now - lastPointTsRef.current <= ANDROID_RESUME_INCREMENTAL_MAX_STALE_MS;
          androidNativePollNowRef.current({
            reason: canResumeIncremental
              ? "resume_partial_buffer_skip_delta"
              : "resume_partial_buffer_skip",
            force: true,
            reset: !canResumeIncremental,
          });
        }
        return;
      }

      if (
        androidResumeSyncInFlightRef.current &&
        filteredPoints.length < ANDROID_BUFFER_DIRECT_APPLY_MIN
      ) {
        return;
      }

      let bufferMinTs = 0;
      for (const p of filteredPoints) {
        const ts = Number(p?.ts || p?.time || 0);
        if (!ts) continue;
        if (!bufferMinTs || ts < bufferMinTs) bufferMinTs = ts;
      }
      const lastPointTs = lastPointTsRef.current || 0;
      const bufferHasOld =
        isRecording && lastPointTs && bufferMinTs && bufferMinTs < lastPointTs - 5000;

      if (bufferHasOld && isRecording && androidNativePollNowRef.current) {
        const freshPoints = filteredPoints.filter((p) => {
          const ts = Number(p?.ts || p?.time || 0);
          return ts && ts > lastPointTs;
        });
        if (freshPoints.length) {
          filteredPoints = freshPoints;
          if (
            !lastGpsBufferLogTsRef.current ||
            now - lastGpsBufferLogTsRef.current > 3000
          ) {
            lastGpsBufferLogTsRef.current = now;
            logEvent("gps", "ANDROID_BUFFER_OLD_TRIMMED", {
              count: points.length,
              filteredCount: filteredPoints.length,
              lastPointTs,
              bufferMinTs,
            });
          }
        } else {
        const lastResync = lastFullResyncTsRef.current || 0;
        if (!lastResync || now - lastResync > 6000) {
          lastFullResyncTsRef.current = now;
          pendingBufferRef.current = [];
          androidNativePollNowRef.current({
            reason: "buffer_resync",
            reset: true,
            force: true,
          });
        }
        return;
        }
      }

      if (isRecording && recentFix && !isRecentResume && !bufferHasOld) {
        return;
      }
      if (!isRecentResume && now - lastPathSyncTsRef.current < 3000 && !bufferHasOld) {
        pendingBufferRef.current = [];
        return;
      }
      const count = points.length;
      const filteredCount = filteredPoints.length;
      const logNow = Date.now();
      if (!lastGpsBufferLogTsRef.current || logNow - lastGpsBufferLogTsRef.current > 3000) {
        lastGpsBufferLogTsRef.current = logNow;
        const sample = (filteredPoints && filteredPoints.length)
          ? { first: filteredPoints[0] && { lat: filteredPoints[0].lat, lon: filteredPoints[0].lon, ts: filteredPoints[0].ts || filteredPoints[0].time || null, seq: filteredPoints[0].seq || null }, last: filteredPoints[filteredPoints.length - 1] && { lat: filteredPoints[filteredPoints.length - 1].lat, lon: filteredPoints[filteredPoints.length - 1].lon, ts: filteredPoints[filteredPoints.length - 1].ts || filteredPoints[filteredPoints.length - 1].time || null, seq: filteredPoints[filteredPoints.length - 1].seq || null } }
          : null;
        logEvent("gps", "Android gps:buffer", { count, filteredCount, sample });
        liveLog("gps:buffer", { count, filteredCount });
      }

      if (!storeState?.isRecording && filteredPoints.length) {
        pendingBufferRef.current = [];
        const shareState = useShareStore.getState();
        const isSharing = !!shareState?.isSharing;
        const groupState = useGroupStore.getState();
        const gid =
          typeof groupState?.currentGroup === "string"
            ? groupState.currentGroup
            : groupState?.currentGroup?.id || null;
        const { events } = useEventsStore.getState() || {};
        const { activeEvent, isLive } = getActiveEventForGroupSafe(
          groupState?.groups,
          gid,
          events
        );

        if (isSharing || isLive) {
          const last = filteredPoints[filteredPoints.length - 1];
          const fix = toPoint(last);
          if (fix && typeof fix.lat === "number" && typeof fix.lon === "number") {
            storeState.setUserPosition({
              lat: fix.lat,
              lon: fix.lon,
              ele: Number.isFinite(fix.ele) ? fix.ele : null,
              acc: Number.isFinite(fix.acc) ? fix.acc : null,
              spd: Number.isFinite(fix.spd) ? fix.spd : null,
              ts: fix.ts || Date.now(),
            });
            liveLog("gps:buffer->live", {
              lat: fix.lat,
              lon: fix.lon,
              acc: fix.acc,
              isLive,
              isSharing,
            });
            if (isLive && activeEvent) {
              syncLiveMemberLocation(fix);
            }
            if (isSharing) {
              const metrics =
                typeof storeState.getMetrics === "function"
                  ? storeState.getMetrics()
                  : null;
              shareState.updateShareData({
                lat: fix.lat,
                lon: fix.lon,
                ele: fix.ele ?? null,
                acc: fix.acc ?? null,
                spd: fix.spd ?? null,
                ts: fix.ts || Date.now(),
                stats: metrics || {},
              });
            }
          }
        }
        logEvent("gps", "ANDROID_BUFFER_DROP_NOT_RECORDING", {
          count: points.length,
          filteredCount: filteredPoints.length,
          isSharing,
          isLive,
          reason: "gps_buffer_guard",
        });
        return;
      }

      if (filteredPoints.length) consumeBuffer(filteredPoints);
    };
    window.addEventListener("gps:buffer", onGpsBuffer);

    const onGpsBufferClear = () => {
      if (!isAndroidRef.current) return;
      pendingBufferRef.current = [];
      lastBufferSeqRef.current = -1;
      lastSeqRef.current = -1;
      lastPointTsRef.current = 0;
      androidNativeSeqRef.current = -1;
      androidNativeLastPointRef.current = null;
      androidFullResyncPendingRef.current = false;
      androidResumeSyncInFlightRef.current = false;
      if (androidDeferredResumeSyncTimerRef.current) {
        clearTimeout(androidDeferredResumeSyncTimerRef.current);
        androidDeferredResumeSyncTimerRef.current = null;
      }
      if (androidQueuedFullResyncTimerRef.current) {
        clearTimeout(androidQueuedFullResyncTimerRef.current);
        androidQueuedFullResyncTimerRef.current = null;
      }
      logEvent("gps", "Android gps:buffer-clear");
    };
    window.addEventListener("gps:buffer-clear", onGpsBufferClear);
    const pollAndroidNative = async (opts = {}) => {
      if (!isAndroidRef.current) return;
      const pollReason = String(opts.reason || "");
      // Android WebView can keep document.visibilityState="visible" while the
      // native app is backgrounded. A foreground interval started in that
      // state can monopolize the JS thread just as the app resumes.
      if (!appActiveRef.current && !opts.force) return;
      if (
        pollReason === "fg" &&
        androidResumeTsRef.current &&
        Date.now() - androidResumeTsRef.current < ANDROID_RESUME_FG_POLL_QUIET_MS
      ) {
        return;
      }
      const { isRecording, isRecordingPaused } = usePanelsStore.getState();
      if (!isRecording || isRecordingPaused) {
        if (!isRecording) {
          androidFullResyncPendingRef.current = false;
          androidResumeSyncInFlightRef.current = false;
          if (androidDeferredResumeSyncTimerRef.current) {
            clearTimeout(androidDeferredResumeSyncTimerRef.current);
            androidDeferredResumeSyncTimerRef.current = null;
          }
          if (androidQueuedFullResyncTimerRef.current) {
            clearTimeout(androidQueuedFullResyncTimerRef.current);
            androidQueuedFullResyncTimerRef.current = null;
          }
        }
        return;
      }
      const isResumeSync = pollReason.startsWith("resume") || !!opts.resumeSync;
      if (androidNativePollBusyRef.current) {
        if (opts.reset) {
          androidFullResyncPendingRef.current = true;
        }
        logEvent("gps", "ANDROID_NATIVE_POLL_SKIPPED_BUSY", {
          reason: opts.reason || "unknown",
          queuedFull: !!opts.reset,
          resumeSync: isResumeSync,
        });
        return;
      }
      const plugin =
        window?.Capacitor?.Plugins?.GPSNativePlugin ||
        window?.Capacitor?.Plugins?.GPSNative;
      if (!plugin?.getRecordingSnapshotSince) {
        if (opts.reset || isResumeSync) {
          androidResumeSyncInFlightRef.current = false;
          androidFullResyncPendingRef.current = false;
        }
        return;
      }
      androidNativePollBusyRef.current = true;
      const pollStartedAt = Date.now();
      const isFullResync = !!opts.reset;
      if (isFullResync) {
        androidFullResyncPendingRef.current = false;
      }
      try {
        const seq = opts.reset ? -1 : (androidNativeSeqRef.current ?? -1);
        const res = await plugin.getRecordingSnapshotSince({ seq });
        const rawPoints = Array.isArray(res?.points) ? res.points : [];
        const liveState = usePanelsStore.getState();
        if (!liveState?.isRecording || liveState?.isRecordingPaused) {
          androidResumeSyncInFlightRef.current = false;
          androidFullResyncPendingRef.current = false;
          logEvent("gps", "ANDROID_NATIVE_POLL_RESULT_IGNORED", {
            reason: opts.reason || "unknown",
            requestedSeq: seq,
            received: rawPoints.length,
            isRecording: !!liveState?.isRecording,
            isPaused: !!liveState?.isRecordingPaused,
          });
          return;
        }
        if (rawPoints.length) {
          consumeAndroidNativePoints(rawPoints, {
            reset: isFullResync || seq < 0,
            reason: opts.reason || "android_native_poll",
            resumeIncremental: isResumeSync && !isFullResync,
          });
        }
        if (isFullResync || isResumeSync) {
          androidResumeSyncInFlightRef.current = false;
        }
        const shouldLogPollPerf =
          rawPoints.length > 0 ||
          pollReason !== "fg" ||
          Date.now() - androidLastEmptyPollPerfLogTsRef.current >= 30000;
        if (shouldLogPollPerf) {
          if (!rawPoints.length && pollReason === "fg") {
            androidLastEmptyPollPerfLogTsRef.current = Date.now();
          }
          logEvent("gps", "ANDROID_NATIVE_POLL_PERF", {
            reason: opts.reason || "unknown",
            requestedSeq: seq,
            received: rawPoints.length,
            nativeReadMs: Number(res?.readMs || 0),
            scannedLines: Number(res?.scannedLines || 0),
            incremental: !!res?.incremental,
            totalMs: Date.now() - pollStartedAt,
            pointsAfter: Array.isArray(usePanelsStore.getState()?.points)
              ? usePanelsStore.getState().points.length
              : 0,
          });
        }
      } catch (err) {
        if (isFullResync || isResumeSync) {
          androidFullResyncPendingRef.current = true;
        }
        logEvent("warn", "ANDROID_NATIVE_POLL_PERF_FAIL", {
          reason: opts.reason || "unknown",
          totalMs: Date.now() - pollStartedAt,
          err: err?.message || String(err),
        });
      } finally {
        androidNativePollBusyRef.current = false;
        if (androidFullResyncPendingRef.current && !androidQueuedFullResyncTimerRef.current) {
          androidQueuedFullResyncTimerRef.current = setTimeout(() => {
            androidQueuedFullResyncTimerRef.current = null;
            const state = usePanelsStore.getState();
            if (!state?.isRecording || state?.isRecordingPaused) {
              androidFullResyncPendingRef.current = false;
              androidResumeSyncInFlightRef.current = false;
              return;
            }
            const elapsedFullMs =
              Date.now() - Number(androidLastFullResumeSyncAtRef.current || 0);
            if (elapsedFullMs < ANDROID_RESUME_FULL_MIN_INTERVAL_MS) {
              androidFullResyncPendingRef.current = false;
              logEvent("gps", "ANDROID_NATIVE_QUEUED_FULL_SKIP_RECENT", {
                elapsedFullMs,
                minIntervalMs: ANDROID_RESUME_FULL_MIN_INTERVAL_MS,
              });
              return;
            }
            androidLastFullResumeSyncAtRef.current = Date.now();
            pollAndroidNative({
              reason: "queued_full_resync",
              force: true,
              reset: true,
            });
          }, 900);
        }
      }
    };

    const flushAndroidCompactResume = async (context = {}) => {
      if (!isAndroidRef.current) return;
      const plugin =
        window?.Capacitor?.Plugins?.GPSNativePlugin ||
        window?.Capacitor?.Plugins?.GPSNative;
      if (!plugin?.flushBufferedLocationsCompact) return false;

      const startedAt = Date.now();
      try {
        const res = await plugin.flushBufferedLocationsCompact({
          maxPoints: ANDROID_RESUME_COMPACT_MAX_POINTS,
        });
        const compactPoints = Array.isArray(res?.points) ? res.points : [];
        if (compactPoints.length) {
          consumeAndroidNativePoints(compactPoints, {
            reset: false,
            reason: "resume_compact_buffer",
            forceRebuild: true,
            resumeIncremental: true,
          });
        }
        logEvent("gps", "ANDROID_RESUME_COMPACT_PERF", {
          source: context.source || "unknown",
          rawCount: Number(res?.meta?.rawCount || 0),
          outputCount: compactPoints.length,
          nativeMs: Number(res?.meta?.readMs || 0),
          totalMs: Date.now() - startedAt,
          pointsAfter: Array.isArray(usePanelsStore.getState()?.points)
            ? usePanelsStore.getState().points.length
            : 0,
        });
        return compactPoints.length > 0;
      } catch (err) {
        logEvent("warn", "ANDROID_RESUME_COMPACT_FAIL", {
          source: context.source || "unknown",
          totalMs: Date.now() - startedAt,
          err: err?.message || String(err),
        });
        return false;
      }
    };

    const startAndroidNativePoll = () => {
      if (!isAndroidRef.current || androidNativePollTimerRef.current) return;
      androidNativePollTimerRef.current = setInterval(() => {
        pollAndroidNative({ reason: "fg" });
      }, ANDROID_NATIVE_POLL_MS);
    };

    const stopAndroidNativePoll = () => {
      if (!androidNativePollTimerRef.current) return;
      clearInterval(androidNativePollTimerRef.current);
      androidNativePollTimerRef.current = null;
    };

    if (isAndroidRef.current) {
      window.__TREKKIN_ANDROID_FLUSH_RECORDING_POINTS = async (reason = "manual") => {
        const pending = pendingBufferRef.current.splice(0);
        if (pending.length) {
          consumeAndroidNativePoints(pending, { reason: `${reason}_pending_buffer` });
        }
        await pollAndroidNative({
          reason: `${reason}_native_snapshot`,
          force: true,
          reset: true,
        });
        logEvent("gps", "ANDROID_RECORDING_POINTS_FLUSH", {
          pending: pending.length,
          reason,
          pointsAfter: Array.isArray(usePanelsStore.getState()?.points)
            ? usePanelsStore.getState().points.length
            : 0,
        });
      };
      androidNativePollNowRef.current = pollAndroidNative;
      androidCompactResumeNowRef.current = flushAndroidCompactResume;
      if (usePanelsStore.getState().isRecording) {
        androidNativeSeqRef.current = -1;
        androidNativeLastPointRef.current = null;
        startAndroidNativePoll();
        androidDeferredResumeSyncTimerRef.current = setTimeout(() => {
          androidDeferredResumeSyncTimerRef.current = null;
          pollAndroidNative({
            reason: "resume_init_deferred",
            force: true,
            reset: true,
            deferred: true,
          });
        }, ANDROID_RESUME_FULL_DEFER_MS);
      }
    }

    const unsubRecording = usePanelsStore.subscribe((state, prev) => {
      if (!prev?.isRecording && state?.isRecording) {
        if (isAndroidRef.current) {
          sharedAndroidRecentPointKeys.clear();
          sharedAndroidElevationFilter.value = null;
          sharedAndroidElevationFilter.ts = 0;
          emitAndroidRender([], true);
          androidRecordingWarmupRef.current = true;
          androidRecordingStartTsRef.current = Date.now();
          androidRecordingWarmupOkTsRef.current = 0;
          androidPathActiveTsRef.current = 0;
          const minTs = getRecordingMinTs();
          pendingBufferRef.current = [];
          lastBufferSeqRef.current = -1;
          lastSeqRef.current = -1;
          lastPointTsRef.current = minTs || 0;
          androidNativeSeqRef.current = -1;
          androidNativeLastPointRef.current = null;
          startAndroidNativePoll();
          pollAndroidNative({ reason: "start", force: true, reset: true });
        }
        drainPendingBuffer();
      } else if (prev?.isRecording && !state?.isRecording) {
        androidRecordingWarmupRef.current = false;
        androidRecordingStartTsRef.current = 0;
        androidRecordingWarmupOkTsRef.current = 0;
        androidPathActiveTsRef.current = 0;
        if (isAndroidRef.current) {
          pendingBufferRef.current = [];
          lastBufferSeqRef.current = -1;
          lastSeqRef.current = -1;
          lastPointTsRef.current = 0;
          androidNativeSeqRef.current = -1;
          androidNativeLastPointRef.current = null;
          androidResumeSyncInFlightRef.current = false;
          androidFullResyncPendingRef.current = false;
          if (androidDeferredResumeSyncTimerRef.current) {
            clearTimeout(androidDeferredResumeSyncTimerRef.current);
            androidDeferredResumeSyncTimerRef.current = null;
          }
          if (androidQueuedFullResyncTimerRef.current) {
            clearTimeout(androidQueuedFullResyncTimerRef.current);
            androidQueuedFullResyncTimerRef.current = null;
          }
          emitAndroidRender([], true);
          stopAndroidNativePoll();
          logEvent("gps", "ANDROID_RECORDING_PIPELINE_STOPPED", {
            reason: "recording_state_false",
          });
        }
      }
    });
    if (Array.isArray(window.__TREKKIN_GPS_BUFFER)) {
      const pending = window.__TREKKIN_GPS_BUFFER;
      window.__TREKKIN_GPS_BUFFER = [];
      consumeBuffer(pending);
    }

    return () => {
      mountedRef.current = false;
      window.removeEventListener("gps:update", onGpsUpdate);
      window.removeEventListener("gps:buffer", onGpsBuffer);
      window.removeEventListener("gps:buffer-clear", onGpsBufferClear);
      if (bufferDrainTimerRef.current) {
        clearInterval(bufferDrainTimerRef.current);
        bufferDrainTimerRef.current = null;
      }
      try {
        unsubRecording?.();
      } catch {}
      if (androidDeferredResumeSyncTimerRef.current) {
        clearTimeout(androidDeferredResumeSyncTimerRef.current);
        androidDeferredResumeSyncTimerRef.current = null;
      }
      if (androidQueuedFullResyncTimerRef.current) {
        clearTimeout(androidQueuedFullResyncTimerRef.current);
        androidQueuedFullResyncTimerRef.current = null;
      }
      stopAndroidNativePoll();
      if (isAndroidRef.current) {
        androidNativePollNowRef.current = null;
        androidCompactResumeNowRef.current = null;
        if (window.__TREKKIN_ANDROID_FLUSH_RECORDING_POINTS) {
          delete window.__TREKKIN_ANDROID_FLUSH_RECORDING_POINTS;
        }
      }
      console.log("🔻 usePanelsGPSBridge desmontado v2.4.0");
    };
  }, []);
}
